/**
 * feedback-adapter.ts — feedback 域「真 API DTO → mock FeedbackRow」塑形
 *
 * 接真改造：真实后端返回 snake_case 规范化 DTO（FeedbackListItem / FeedbackDetailResponse /
 * FeedbackReplyResponse，见 packages/shared/src/schemas/feedback.ts），而 FeedbackPage 仍消费
 * 原型 camelCase 富集行 FeedbackRow（见 ~/data/mock/types.ts）。本 adapter 做字段对照 + 缺字段补默认，
 * 让页面 100% 不动、设计还原不变，数据变真。
 *
 * 缺字段处理（真 API 没有、mock 有）：
 * - user：真 API 列表/详情现含 user_name（users.display_name，server left join 解析）→ 用作 user.name；
 *   富集详情（email/country/totalSpend 等）仍由 opts.user（N+1 拉 user 详情）注入；未传则最简占位
 *   （name 取真实 user_name，缺名回退 user_id；其余字段空串/0/null，页面只读字段不崩）。
 * - contentZh：真 API 无「中文翻译」字段（翻译是 service 侧能力，DTO 不回传）→ 留空字符串。
 * - replies：mock 是 number（回复条数）→ 详情用 detail.replies.length；列表无 replies 字段 → 0。
 * - appId：真 feedback DTO 现含 app_id（catch-up §1，ADM-FBK-S001，仅 admin 暴露）→ 直接用 item.app_id；
 *   opts.appId 仅作显式 override。appName：DTO 现含 app_name（apps.name，server left join app_id 解析）→
 *   直接用 item.app_name；opts.appName 仅作显式 override，缺省再回退 platform。
 * - lang：真 feedback DTO 现含 language（ADM-FBK-S003，运营核对 auto_translate 目标）→ 直接用 item.language；
 *   opts.lang 仅作显式覆盖（缺省回退 item.language，再缺省空串）。
 * - subject / category / contact_email：真 API 均有，直接透传（category 闭集 PAYMENT/CONTENT/BUG/
 *   SUGGESTION/ACCOUNT/OTHER；contactEmail 供列表项与详情头展示）。
 * - autoClosed：ADM-FBK-S004 接真——列表 DTO 现含 auto_closed_by（server 由 status_log 最近 CLOSED 转换 actor_type 派生），
 *   autoClosed = (auto_closed_by==='SYSTEM')；closedAt 仍无源 → 不设（optional 缺省）。
 */

import type {
  FeedbackDetailResponse,
  FeedbackListItem,
  FeedbackReplyResponse,
} from '@novel-admin/shared';
import type { FeedbackRow, UserRow } from '~/data/mock/types';

/**
 * 真 API feedback status → mock 中文状态。
 * dev-docs 90-反馈 §4.3 状态机闭集：OPEN / IN_PROGRESS / REPLIED / RESOLVED / CLOSED。
 * REPLIED→「已回复」、RESOLVED/CLOSED→「已关闭」对齐页面 seg 分组。
 * PENDING 非闭集成员，仅作防御性 legacy 兜底（历史误写行映射「处理中」，避免 seg 漏算）。
 */
const FEEDBACK_STATUS_ZH: Record<string, string> = {
  OPEN: '待回复',
  IN_PROGRESS: '处理中',
  REPLIED: '已回复',
  RESOLVED: '已关闭',
  CLOSED: '已关闭',
  PENDING: '处理中', // legacy 兜底（闭集外，历史误写防御）
};

/** 未知状态兜底为「待回复」，避免页面 seg 计数漏算。 */
function toFeedbackStatusZh(status: string): string {
  return FEEDBACK_STATUS_ZH[status] ?? '待回复';
}

/** adapter 可选注入项：真 API 缺失但页面需要的富集字段，由调用方（hook / N+1）补齐。 */
export type FeedbackAdapterOptions = {
  /** 该反馈的提交用户（N+1 拉 users 详情后塑形传入）；缺省则用 user_id 最简占位。 */
  user?: UserRow;
  /** App 维度 override（DTO 现含真 app_id，catch-up §1）；缺省用 item.app_id。 */
  appId?: string;
  appName?: string;
  /** 反馈实际语言（真 feedback DTO 无 lang，仅 platform）：调用方注入；缺省空串。 */
  lang?: string;
};

/**
 * user_id (+ 真实 user_name) → 最简 UserRow 占位：id 真实、name 取 server 解析的真实显示名（缺则回退 user_id），
 * 其余字段空串/0/null（页面对缺省字段只读不崩）。
 */
function fallbackUser(userId: string | null, userName?: string | null): FeedbackRow['user'] {
  return {
    id: userId,
    // 此 fallback 没有展示 ID 的事实来源；绝不由内部 UUID 编造。
    displayUserId: '',
    name: userId === null ? '设备反馈' : (userName ?? userId),
    email: '',
    appId: '',
    appName: '',
    country: '',
    lang: '',
    registeredAt: '',
    lastSeen: '',
    coin: 0,
    bonus: 0,
    // UserRow.totalSpend 现为 per-currency 展示串（catch-up #130）；feedback 占位用户无充值数据 → '—'
    totalSpend: '—',
    status: '',
    risk: null,
    plan: '',
  };
}

/** 列表项 → FeedbackRow（无 replies 明细 → 0；无 contentZh/详情正文 → 用 subject 占位正文）。 */
export function adaptFeedbackListItem(
  item: FeedbackListItem,
  opts: FeedbackAdapterOptions = {},
): FeedbackRow {
  const user =
    item.user_id === null
      ? fallbackUser(null)
      : (opts.user ?? fallbackUser(item.user_id, item.user_name));
  return {
    id: item.id,
    // catch-up §1：列表 DTO 现含真 app_id（仅 admin 暴露）；opts.appId 仅作兼容 override
    appId: opts.appId ?? item.app_id,
    // app_name 已接真（apps.name，server left join）；opts.appName 仅显式 override，缺省回退 platform
    appName: opts.appName ?? item.app_name ?? item.platform,
    user,
    subject: item.subject,
    // 列表 DTO 不含正文，用 subject 占位（详情页会用 adaptFeedbackDetail 拿到完整 content）
    content: item.subject,
    contentZh: '', // 真 API 无翻译字段
    lang: opts.lang ?? item.language ?? '', // 反馈实际语言（ADM-FBK-S003 auto_translate 目标）
    status: toFeedbackStatusZh(item.status),
    createdAt: item.created_at,
    replies: 0,
    category: item.category,
    // server #189：contact_email 可空（未勾选邮件通知）→ undefined（FeedbackRow 用 optional 口径）
    contactEmail: item.contact_email ?? undefined,
    // ADM-FBK-S004：auto_closed_by==='SYSTEM'（cron 自动关单）→ autoClosed=true，供「自动关闭」tab 筛选
    autoClosed: item.auto_closed_by === 'SYSTEM',
  };
}

/** 详情 → FeedbackRow（replies = detail.replies.length；content 取首条用户消息缺省用 subject）。 */
export function adaptFeedbackDetail(
  detail: FeedbackDetailResponse,
  opts: FeedbackAdapterOptions = {},
): FeedbackRow {
  const user =
    detail.user_id === null
      ? fallbackUser(null)
      : (opts.user ?? fallbackUser(detail.user_id, detail.user_name));
  return {
    id: detail.id,
    // catch-up §1：详情 DTO 现含真 app_id（仅 admin 暴露）；opts.appId 仅作兼容 override
    appId: opts.appId ?? detail.app_id,
    // app_name 已接真（apps.name，server left join）；opts.appName 仅显式 override，缺省回退 platform
    appName: opts.appName ?? detail.app_name ?? detail.platform,
    user,
    subject: detail.subject,
    // 反馈正文取 description（feedback.description 必填正文）；subject 仅短标题
    content: detail.description,
    contentZh: '', // 真 API 无翻译字段
    lang: opts.lang ?? detail.language ?? '', // 反馈实际语言（ADM-FBK-S003 auto_translate 目标）
    status: toFeedbackStatusZh(detail.status),
    createdAt: detail.created_at,
    replies: detail.replies.length,
    category: detail.category,
    // server #189：contact_email 可空（未勾选邮件通知）→ undefined（FeedbackRow 用 optional 口径）
    contactEmail: detail.contact_email ?? undefined,
  };
}

/**
 * 单条回复塑形：mock 页面当前仅用 replies 计数（number），未渲染回复明细列表。
 * 这里把真 API 的 FeedbackReplyResponse 透传出去备用（页面后续接入回复明细时直接消费），
 * 不做 camelCase 改名以免与未来契约漂移——保持 snake_case 原 DTO。
 */
export function adaptFeedbackReply(reply: FeedbackReplyResponse): FeedbackReplyResponse {
  return reply;
}
